import React, { useEffect } from 'react';
import MerchantRegistrationForm from 'components/MerchantRegistrationForm';
import { DocumentUploadId } from 'types/Document';
import DisplayText from 'components/ui-kits/DisplayText';
import {
  Banner,
  FooterNote
} from 'components/UploadMerchantDocument/UploadMerchantDocument.style';
import ListLayout from 'components/ListLayout';
import imgSrc from '../../../assets/imageBannerOnlinePayment.png';
import bannerDecoration from '../../../assets/bannerDecoration.png';
import { TextDecoration, UpperNoteWrapper } from './OnlinePayment.style';
import { Merchant } from 'types/Merchant';
import { useQueryClient } from 'react-query';
import { MERCHANT_TYPE } from 'const/Const';
import { formatPercent, getPreferPlan, replaceDotToComma } from 'utils';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { useHistory } from 'react-router-dom';

const tableRows: {
  id: DocumentUploadId;
  label: string;
  isRequired: boolean;
}[] = [
  {
    id: 'online_payment_and_visible_on_merchant_list_iban',
    label: "IBAN* (Relevé d'identité bancaire)",
    isRequired: true
  },
  {
    id: 'online_payment_and_visible_on_merchant_list_accounting_report',
    label:
      'Dernier bilan disponible* (Budget prévisionnel si pas de bilan disponible)',
    isRequired: true
  },
  {
    id: 'online_payment_and_visible_on_merchant_list_business_status',
    label: 'Statut juridique*',
    isRequired: true
  }
];

const SCREEN_NAME = 'online_payment_and_visible_on_merchant_list';

const renderAdditionalNotesAbove = ({
  isFreePlan,
  isSpecialMerchant,
  subscriptionPercentage,
  subscriptionFixedAmount,
  subscriptionMiniumAmount
}: {
  isFreePlan: boolean;
  isSpecialMerchant: boolean;
  subscriptionPercentage: number;
  subscriptionFixedAmount: number;
  subscriptionMiniumAmount: number;
}) => {
  const percentage = formatPercent(String(subscriptionPercentage * 100), 1);
  const fixedAmount = replaceDotToComma(subscriptionFixedAmount);
  const minimumAmount = subscriptionMiniumAmount;

  return (
    <>
      <Banner>
        <ListLayout>
          <DisplayText
            fontSize="XXXL"
            color="WHITE"
            fontWeight="LIGHT"
            margin="2.5rem auto 0">
            Activez le paiement par{' '}
            <TextDecoration>
              carte bancaire
              <img
                className="decoration"
                alt="text decoration"
                src={bannerDecoration}
              />
            </TextDecoration>
          </DisplayText>
          <DisplayText
            fontSize="XXL"
            color="WHITE"
            fontWeight="BOLD"
            margin="2.5rem auto 3rem">
            Et profitez de <br />
            nombreux avantages :
          </DisplayText>
        </ListLayout>
      </Banner>
      <UpperNoteWrapper>
        <ListLayout>
          {isFreePlan ? (
            <>
              <DisplayText lineHeight="1.5" fontSize="L" margin="4rem 0 0 0">
                <p>✅ Sécurisez vos ventes en évitant les impayés</p>
                <p>✅ Un parcours 100% digital pour vos clients</p>
                <p>
                  ✅ La mise en place de votre boutique offerte (249€ --&gt; 0€)
                </p>
                <p>
                  ✅ Une visibilité accrue grâce au référencement sur la place
                  de marché Achil
                </p>
              </DisplayText>
            </>
          ) : (
            <>
              <DisplayText lineHeight="1.5" fontSize="L" margin="4rem 0 0 0">
                <p>✅ Sécurisez vos ventes en évitant les impayés</p>
                <p>✅ Un parcours 100% digital pour vos clients</p>
                <p>
                  ✅ La mise en place de votre boutique offerte (199€ --&gt; 0€)
                </p>
                <p>
                  ✅ Une visibilité accrue grâce au référencement sur la place
                  de marché Achil
                </p>
              </DisplayText>
            </>
          )}
          <div className="fee-detail">
            <DisplayText fontSize="L" margin="0 0 1rem 0">
              Des frais de transaction{' '}
              <span className="bold text-blue">simples</span> et{' '}
              <span className="bold text-red">transparents</span> :
            </DisplayText>
            {isSpecialMerchant ? (
              <>
                <DisplayText fontSize="L">
                  - <strong>{percentage} % HT</strong> / transaction ≤ à{' '}
                  {minimumAmount}€
                </DisplayText>
                <DisplayText fontSize="L">
                  -{' '}
                  <strong>
                    {' '}
                    {percentage} % + {fixedAmount}€ HT
                  </strong>{' '}
                  / transaction &gt; {minimumAmount}€
                </DisplayText>
              </>
            ) : (
              <>
                <DisplayText fontSize="L">
                  -{' '}
                  <strong>
                    {percentage} % + {fixedAmount}€ HT
                  </strong>{' '}
                  / transaction
                </DisplayText>
              </>
            )}
          </div>
          <DisplayText
            lineHeight="1.5"
            fontSize="L"
            margin={isSpecialMerchant ? '4rem 0 1.5rem 0' : '4rem 0'}
            fontStyle="italic">
            <p>
              Chaque lundi, les revenus de vos paiements en ligne de la semaine
              précédente, déduction faite des frais de transaction, sont virés
              sur votre compte bancaire. Vous recevez en même temps la facture
              correspondante.
            </p>
            Vous souhaitez activer le paiement en ligne ? Il vous suffit de
            transmettre les justificatifs ci-dessous :
          </DisplayText>
        </ListLayout>
        <img className="decoration-image" src={imgSrc} />
      </UpperNoteWrapper>
    </>
  );
};

const additionalNotesBelow = (
  <>
    <ListLayout>
      <FooterNote>
        <DisplayText fontSize="L" color="WHITE">
          Une fois vos justificatifs téléchargés et soumis, <br />{' '}
          <span className="bold">notre équipe les vérifiera rapidement.</span>
        </DisplayText>
        <DisplayText fontSize="L" color="WHITE">
          <span style={{ fontWeight: 'bold' }}>
            Boostez dès maintenant la visibilité
          </span>{' '}
          de votre boutique et{' '}
          <span style={{ fontWeight: 'bold' }}>sécurisez vos revenus</span> avec
          le paiement en ligne !
        </DisplayText>
      </FooterNote>
    </ListLayout>
  </>
);

const OnlinePayment = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const dataMerchant = queryClient.getQueryData<Merchant>('getMerchant');
  const isFetching = queryClient.isFetching(['getMerchant']) > 0;
  const plan = useSelector((state: RootState) => state.subscriptionPlan);
  const selectedPlan = getPreferPlan(plan, dataMerchant?.subscription);

  const isSpecialMerchant =
    selectedPlan.type === MERCHANT_TYPE.FREE_BOULANGERIES ||
    selectedPlan.type === MERCHANT_TYPE.STANDARD_BOULANGERIES;
  const isFreePlan =
    selectedPlan.type === MERCHANT_TYPE.FREE ||
    selectedPlan.type === MERCHANT_TYPE.FREE_BOULANGERIES;

  useEffect(() => {
    if (isFetching) return;

    if (!selectedPlan?.id) {
      history.push({
        pathname: '/merchant-registration/plans'
      });
    }
  }, [history, selectedPlan?.id, isFetching]);

  return (
    <MerchantRegistrationForm
      tableRows={tableRows}
      screenName={SCREEN_NAME}
      doneStatusLabelButton="Paiement en ligne"
      icon="✅"
      additionalNotesAboveTable={renderAdditionalNotesAbove({
        isFreePlan,
        isSpecialMerchant,
        subscriptionPercentage: selectedPlan.percentage || 0,
        subscriptionFixedAmount: selectedPlan.fixAmount || 0,
        subscriptionMiniumAmount: selectedPlan.minimumAmount || 0
      })}
      additionalNotesBelowTable={additionalNotesBelow}
    />
  );
};

export default OnlinePayment;
